import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components";
import { Tabs, type TabItem } from "@/components/Tabs";
import { Repos } from "@/pages/Tools/Repos";
import { TenderMigration } from "@/pages/Tools/TenderMigration";
import { StudyMigration } from "@/pages/Tools/StudyMigration";
import { UserStories } from "@/pages/Tools/UserStories";
import { RoleMigration } from "@/pages/Tools/RoleMigration";
import { UserMigration } from "@/pages/Tools/UserMigration";
import { DatabaseSync } from "@/pages/Tools/DatabaseSync";

export type ToolTab = "repo" | "tenders" | "hu" | "users" | "database";

export const Tools: React.FC = () => {
    const { t } = useTranslation("global");
    const getInitialTab = (): ToolTab => {
        if (typeof window !== "undefined") {
            const path = window.location.pathname.replace(/\/+$/, "");
            const segments = path.split("/");
            const last = segments[segments.length - 1];
            if (
                last === "tenders" ||
                last === "hu" ||
                last === "users" ||
                last === "database"
            ) {
                return last;
            }
        }
        return "repo";
    };

    const [activeTab, setActiveTab] = useState<ToolTab>(getInitialTab);

    const tabs: TabItem<ToolTab>[] = [
        {
            id: "repo",
            label: t("tools.tabs.repo"),
            icon: "folder",
        },
        {
            id: "tenders",
            label: t("tools.tabs.tenders"),
            icon: "briefcase",
        },
        {
            id: "hu",
            label: t("tools.tabs.hu"),
            icon: "bot",
        },
        {
            id: "users",
            label: t("tools.tabs.users"),
            icon: "users",
        },
        {
            id: "database",
            label: t("tools.tabs.database"),
            icon: "database",
        },
    ];

    const handleTabChange = (tabId: ToolTab): void => {
        setActiveTab(tabId);
    };

    return (
        <div className="page space-y-6">
            <PageHeader
                title={t("tools.title")}
                subtitle={t("tools.description")}
            />

            <Tabs<ToolTab>
                tabs={tabs}
                activeTab={activeTab}
                onChange={handleTabChange}
                variant="underline"
                scroll={true}
                syncUrl={true}
                urlPrefix="/tools"
                urlDefaultTab="repo"
            />

            <div className="pt-2">
                {activeTab === "repo" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <Repos />
                    </div>
                )}

                {activeTab === "tenders" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <TenderMigration />
                        <StudyMigration />
                    </div>
                )}

                {activeTab === "hu" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <UserStories />
                    </div>
                )}

                {activeTab === "users" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <RoleMigration />
                        <UserMigration />
                    </div>
                )}

                {activeTab === "database" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        <DatabaseSync />
                    </div>
                )}
            </div>
        </div>
    );
};
